import React, { useState } from "react";
import { getAsrStationSummary } from "../../services/api";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";
import { toDDMMYYYY } from "../../utils/dateUtils";

const FOP_LABELS: Record<string, string> = {
  CA: "Cash", CC: "Credit Card", CK: "Check", ET: "Exchange Ticket",
  PT: "Pre-Paid", TV: "Travel Voucher", BT: "Travel Bank",
  GR: "Govt Bill", IN: "Involuntary", CT: "Certificate", XX: "Other",
};

function todayISO() { return new Date().toISOString().split("T")[0]; }

function fmtAmt(n: number) {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function safeArray(val: any): any[] {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

function resolveKey(obj: any, ...keys: string[]): any {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k];
  }
  return undefined;
}

function parseAmount(val: any): number {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  const str = typeof val === "string" ? val : (val._ ?? val["#text"] ?? "0");
  return parseFloat(str) || 0;
}

type EmployeeSummary = {
  employeeNumber: string;
  employeeName: string;
  totals: { fopCode: string; currencyCode: string; amount: number }[];
};

type StationTotal = {
  currencyCode: string;
  subTotals: { fopCode: string; amount: number }[];
};

function extractEmployeeSummaries(raw: any): EmployeeSummary[] {
  const employees = safeArray(
    resolveKey(raw, "EmployeeSummary", "ns4:EmployeeSummary", "tkt:EmployeeSummary")
  );
  return employees.map((emp: any) => {
    const attrs = emp.$ ?? emp;
    const fopNodes = safeArray(resolveKey(emp, "SSFop", "ns4:SSFop", "tkt:SSFop", "FopDetail", "ns4:FopDetail"));
    return {
      employeeNumber: attrs.EmployeeNumber ?? resolveKey(emp, "EmployeeNumber", "ns4:EmployeeNumber") ?? "",
      employeeName: attrs.EmployeeName ?? resolveKey(emp, "EmployeeName", "ns4:EmployeeName") ?? "",
      totals: fopNodes.map((f: any) => ({
        fopCode: resolveKey(f, "FopCode", "ns4:FopCode") ?? f.$.FopCode ?? "",
        currencyCode: resolveKey(f, "CurrencyCode", "ns4:CurrencyCode") ?? "",
        amount: parseAmount(resolveKey(f, "TotalAmount", "ns4:TotalAmount")),
      })),
    };
  });
}

function extractStationTotals(raw: any): StationTotal[] {
  const totals = safeArray(
    resolveKey(raw, "TotalDailySales", "ns4:TotalDailySales", "tkt:TotalDailySales", "StationTotal", "ns4:StationTotal")
  );
  return totals.map((t: any) => {
    const currency = resolveKey(t, "CurrencyCode", "ns4:CurrencyCode") ?? "";
    const subArr = safeArray(resolveKey(t, "SubTotal", "ns4:SubTotal"));
    return {
      currencyCode: currency,
      subTotals: subArr.map((s: any) => ({
        fopCode: s.$ ? (s.$.fop ?? s.$.FopCode ?? "") : "",
        amount: parseAmount(s),
      })),
    };
  });
}

const inputCls = "border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary";

const AsrStationSummary: React.FC = () => {
  const [ticketingProvider, setTicketingProvider] = useState("");
  const [stationNumber, setStationNumber]         = useState("");
  const [reportDate, setReportDate]               = useState(todayISO());

  const [loading, setLoading]       = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [result, setResult]         = useState<any | null>(null);

  const canFetch = ticketingProvider.trim() && stationNumber.trim() && reportDate;

  const handleFetch = async () => {
    if (!canFetch) return;
    setLoading(true);
    setFetchError(null);
    setResult(null);
    try {
      const data = await getAsrStationSummary({
        ticketingProvider: ticketingProvider.trim().toUpperCase(),
        stationNumber: stationNumber.trim(),
        reportDate,
      });
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to fetch report."
      );
    } finally {
      setLoading(false);
    }
  };

  const rawData = result?.rawData ?? {};
  const employees = extractEmployeeSummaries(rawData);
  const stationTotals = extractStationTotals(rawData);

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">ASR: Station Summary</h1>
        <p className="text-sm text-gray-500 mt-1">
          Station-level cash reconciliation via Sabre TKT_AsrServicesRQ StationSummaryRQ.
          Requires SabreSonic airline access.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ticketing Provider *</label>
            <input value={ticketingProvider} onChange={(e) => setTicketingProvider(e.target.value.toUpperCase())}
              placeholder="e.g. 7F" maxLength={2} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Station Number *</label>
            <input value={stationNumber} onChange={(e) => setStationNumber(e.target.value)}
              placeholder="8-digit station" maxLength={10} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Report Date *</label>
            <DatePickerWithValidation
              selected={reportDate}
              onSelect={setReportDate}
              maxDate={todayISO()}
              placeholder="dd/mm/yyyy"
            />
          </div>
        </div>
        <div className="mt-4">
          <button onClick={handleFetch} disabled={loading || !canFetch}
            className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50">
            {loading ? "Loading..." : "Fetch Report"}
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">{fetchError}</div>
      )}

      {result && (
        <>
          {/* Meta */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Report Date</span>
              <p className="font-semibold text-heading">{toDDMMYYYY(result.reportDate)}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Station</span>
              <p className="font-semibold text-heading">{result.stationNumber}</p>
            </div>
            {employees.length > 0 && (
              <div>
                <span className="text-gray-500 text-xs uppercase font-semibold">Agents</span>
                <p className="font-bold text-heading text-base">{employees.length}</p>
              </div>
            )}
          </div>

          {/* Station Totals */}
          {stationTotals.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
              <h3 className="text-xs uppercase font-bold text-gray-500 tracking-widest mb-3">Station Totals</h3>
              <div className="flex flex-wrap gap-4">
                {stationTotals.map((dt) => (
                  <div key={dt.currencyCode} className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 min-w-[160px]">
                    <p className="text-xs font-bold text-blue-500 mb-2">{dt.currencyCode}</p>
                    {dt.subTotals.map((st) => (
                      <div key={st.fopCode} className="flex justify-between gap-3 text-sm">
                        <span className="text-gray-500 text-xs">{FOP_LABELS[st.fopCode] ?? st.fopCode}</span>
                        <span className="font-semibold text-heading">{fmtAmt(st.amount)}</span>
                      </div>
                    ))}
                    <div className="border-t border-blue-200 mt-1.5 pt-1.5 flex justify-between text-sm">
                      <span className="text-gray-500 text-xs font-semibold">Total</span>
                      <span className="font-bold text-heading">
                        {fmtAmt(dt.subTotals.reduce((s, x) => s + x.amount, 0))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Employee Summaries Table */}
          {employees.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Employee #</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">FOP</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Currency</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp, idx) => (
                      emp.totals.length > 0 ? emp.totals.map((tot, ti) => (
                        <tr key={`${idx}-${ti}`} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          {ti === 0 && (
                            <>
                              <td className="px-4 py-3 font-mono text-xs font-semibold text-heading whitespace-nowrap" rowSpan={emp.totals.length}>{emp.employeeNumber}</td>
                              <td className="px-4 py-3 font-semibold text-heading whitespace-nowrap" rowSpan={emp.totals.length}>{emp.employeeName || "---"}</td>
                            </>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="bg-gray-100 text-gray-700 text-xs font-semibold rounded px-2 py-0.5" title={FOP_LABELS[tot.fopCode] ?? tot.fopCode}>
                              {tot.fopCode}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{tot.currencyCode}</td>
                          <td className="px-4 py-3 text-right font-semibold text-heading whitespace-nowrap">{fmtAmt(tot.amount)}</td>
                        </tr>
                      )) : (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-heading whitespace-nowrap">{emp.employeeNumber}</td>
                          <td className="px-4 py-3 font-semibold text-heading whitespace-nowrap">{emp.employeeName || "---"}</td>
                          <td className="px-4 py-3 text-gray-400" colSpan={3}>No FOP data</td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fallback: if no structured data was extracted, show raw */}
          {employees.length === 0 && stationTotals.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-xs uppercase font-bold text-gray-500 tracking-widest mb-2">Response Data</h3>
              <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-700 overflow-auto max-h-[500px] whitespace-pre-wrap break-all">
                {JSON.stringify(rawData, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AsrStationSummary;
